/*
 *  -----------------------------------------------------------------------
 *    <copyright file="site-overview-scrollable-grid/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import cn from 'clsx';
import {
    getSiteOverviewColumns,
    getSiteOverviewData,
} from '@components/central-monitoring/study-summary/site-overview-scrollable-grid/site-overview-scrollable-grid.helper';
import {
    SiteOverviewScrollableGridProps,
    SiteRow,
} from '@components/central-monitoring/study-summary/site-overview-scrollable-grid/site-overview-scrollable-grid.interface';
import { OVERVIEW_TABLE_CONFIG_TITLE } from '@components/central-monitoring/study-summary/study-summary.data';
import ExpandableGridV2 from '@components/core/expandable/grid-v2';
import {
    ExcelExportConfig,
    IActionItemV2,
    IExpandableGridColumnV2,
} from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import Loading from '@components/core/loading';
import Error from '@components/error/error';
import { useBoolean, useTranslation } from 'hooks';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { siteOverviewTable, siteOverviewTableVariables } from '@queries/generated/sites.generated';
import { GET_SITE_SUMMARY_OVERVIEW_TABLE } from '@queries/sites';

import '../../reports/kri-scores-grid.scss';
import { AllRoutes } from '@utils/router/all-routes';
import NavigationService from '@components-clean/page-nav/navigation.service';

const SiteOverviewScrollableGrid = ({
    studyData,
    sitesStatusesToExclude,
    gridViewOptions,
    switchMenu,
    inactive,
    studyId,
    currentDataSet,
    previousDataSet,
}: SiteOverviewScrollableGridProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { data, loading, error } = useQuery<siteOverviewTable, siteOverviewTableVariables>(
        GET_SITE_SUMMARY_OVERVIEW_TABLE,
        {
            variables: {
                currentDataSetId: currentDataSet?.isDataset ? currentDataSet.id : currentDataSet?.dataSetId,
                previousDataSetId: previousDataSet.id,
                studyId,
            },
        },
    );
    const [columns, setColumns] = useState<IExpandableGridColumnV2[]>([]);
    const [items, setItems] = useState<Record<string, SiteRow>[]>([]);
    const noConfigurations = useBoolean(false);

    const createDataForGrid = (siteOverviewGrid: SiteRow[]) => {
        const gridData = getSiteOverviewData(siteOverviewGrid);
        const gridColumns = getSiteOverviewColumns(gridData, previousDataSet, currentDataSet, t);

        // NOTE: We should hide first row which contains only titles and data types for columns (config) to build the correct grid.
        if (gridData?.length && gridData[0].siteName === OVERVIEW_TABLE_CONFIG_TITLE) {
            gridData.shift();
        }

        return {
            columns: gridColumns,
            data: gridData,
        };
    };

    useEffect(() => {
        if (data) {
            const isConfigurationEmpty = data?.siteOverviewTable?.every(
                (item) => item?.columnSummary?.length === 0 && item.panelSummary?.length === 0,
            );
            noConfigurations.setValue(!!isConfigurationEmpty);

            const gridOptions = createDataForGrid(data.siteOverviewTable);
            const orderFilteredColumns = orderBy(gridOptions.columns, [{ field: 'order', dir: 'asc' }]);

            setColumns(orderFilteredColumns);
            setItems(gridOptions.data);
        }
    }, [data, previousDataSet]);

    const getSiteRedirectData = (itemName: string) => {
        const site = studyData?.sites?.find((item) => item.Fields['Site.name'] === itemName);

        return {
            id: site?.Fields?.['Site.id'] || null,
            name: itemName,
            status: site?.Fields?.['Site.status'] || null,
        };
    };

    const rowActions = (item: any): IActionItemV2[] => {
        const redirectData = getSiteRedirectData(item.siteName);

        return [
            {
                label: 'cm:viewSiteDashboard',
                icon: ['far', 'eye'],
                onClick: () => {
                    NavigationService.setViewLegacySiteDashboard();
                    navigate(AllRoutes.path('cm-site-dashboard') as string, {
                        state: {
                            skipSelectDate: true,
                            showDashboard: true,
                            siteId: redirectData.id as string | null | undefined,
                        },
                    });
                },
                enabled: sitesStatusesToExclude?.indexOf(redirectData.status) < 0 ?? true,
            },
        ];
    };

    const exportFileName = `${studyData?.study?.Fields?.['Study.number']}_SiteOverview_ComparisonDataset-${
        !previousDataSet.isDefault ? previousDataSet._dataCutDate : ''
    }_Dataset-${stringDateWithoutTimeFromUtc(studyData.dataCutDate, 'DD-MMM-YYYY')}`;

    const exportConfig: ExcelExportConfig = {
        fileName: exportFileName,
        useColumn: true,
        useData: true,
    };

    if (loading) return <Loading float />;

    if (error) return <Error error={error} />;

    if (!loading && !studyData) return <h1>No data available</h1>;

    return (
        <>
            {(inactive || noConfigurations.value) && (
                <p className="warning-message">
                    {inactive ? t('cm:noOverviewTablesSelected') : t('cm:siteOverviewGridWarning')}
                </p>
            )}
            <ExpandableGridV2
                title={t('admin:siteOverview')}
                customKey="site-overview-grid"
                className={cn({ 'grid-inactive': inactive || noConfigurations.value }, 'aligned-headers')}
                id="siteOverviewGrid"
                items={!inactive && !noConfigurations.value ? items : []}
                groupable
                scrollable="scrollable"
                reorderable
                resizable
                pageable={false}
                hideCheckbox
                hideExpand
                gridViewOptions={gridViewOptions}
                switchMenu={switchMenu}
                rowActions={rowActions}
                columns={!inactive && !noConfigurations.value ? columns : []}
                exportConfig={exportConfig}
            />
        </>
    );
};

export default SiteOverviewScrollableGrid;
